/**
 * Invite Acceptance Page
 *
 * Handles workspace invitation acceptance.
 * Shows different states based on authentication status and email matching.
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion } from 'motion/react';
import {
  Loader2,
  Building2,
  CheckCircle,
  AlertCircle,
  LogIn,
  Shield,
  User,
  Eye,
  LogOut,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/client/lib/api';
import { useAuth } from '@/client/hooks/use-auth';
import { signOut } from '@/client/lib/auth-client';
import { Button } from '@/client/components/ui/button';
import { cn } from '@/client/lib/utils';

type WorkspaceRole = 'admin' | 'member' | 'viewer';

interface InvitationDetails {
  invitation: {
    email: string;
    role: WorkspaceRole;
    status: 'pending' | 'accepted' | 'expired';
    expiresAt: string;
    workspace: {
      id: string;
      name: string;
      slug: string;
    };
  };
}

interface AcceptResponse {
  success: boolean;
  membership: {
    workspaceId: string;
    role: string;
    joinedAt: string;
  };
}

const ROLE_CONFIG: Record<
  WorkspaceRole,
  { label: string; icon: typeof Shield; color: string; description: string }
> = {
  admin: {
    label: 'Admin',
    icon: Shield,
    color: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    description: 'Full access, can manage members',
  },
  member: {
    label: 'Member',
    icon: User,
    color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
    description: 'Create and edit links',
  },
  viewer: {
    label: 'Viewer',
    icon: Eye,
    color: 'bg-slate-500/10 text-slate-600 border-slate-500/20',
    description: 'Read-only access',
  },
};

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const [isAccepting, setIsAccepting] = useState(false);

  // Fetch invitation details
  const {
    data,
    isLoading: inviteLoading,
    error,
  } = useQuery<InvitationDetails>({
    queryKey: ['invitation', token],
    queryFn: async () => {
      const response = await apiFetch(`/api/invitations/${token}`, {
        skipWorkspaceHeader: true,
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Invitation not found');
      }
      return response.json();
    },
    enabled: !!token,
    retry: false,
  });

  // Accept invitation mutation
  const acceptMutation = useMutation({
    mutationFn: async () => {
      const response = await apiFetch(`/api/invitations/${token}/accept`, {
        method: 'POST',
        skipWorkspaceHeader: true,
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to accept invitation');
      }
      return response.json() as Promise<AcceptResponse>;
    },
    onSuccess: () => {
      toast.success('You have joined the workspace!');
      // Navigate to dashboard after short delay
      setTimeout(() => {
        navigate('/links');
      }, 1000);
    },
    onError: (error: Error) => {
      toast.error(error.message);
      setIsAccepting(false);
    },
  });

  const handleAccept = () => {
    setIsAccepting(true);
    acceptMutation.mutate();
  };

  const handleSignOut = async () => {
    await signOut();
    // After sign out, the page will re-render with unauthenticated state
  };

  const isLoading = authLoading || inviteLoading;
  const invitation = data?.invitation;

  // Determine the state
  const isExpired = invitation?.status === 'expired';
  const isAlreadyAccepted = invitation?.status === 'accepted';
  const emailMatches = user?.email?.toLowerCase() === invitation?.email.toLowerCase();

  // Render loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Render error state
  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md text-center"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-destructive/10 flex items-center justify-center">
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Invitation Not Found</h1>
            <p className="text-muted-foreground mb-6">
              This invitation link is invalid or has been removed.
            </p>
            <Button asChild>
              <Link to="/">Go to Homepage</Link>
            </Button>
          </motion.div>
        </main>
      </div>
    );
  }

  // Render expired state
  if (isExpired) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md text-center"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-amber-500/10 flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Invitation Expired</h1>
            <p className="text-muted-foreground mb-6">
              This invitation has expired. Please ask the workspace owner to send a new one.
            </p>
            <Button asChild variant="outline">
              <Link to="/">Go to Homepage</Link>
            </Button>
          </motion.div>
        </main>
      </div>
    );
  }

  // Render already accepted state
  if (isAlreadyAccepted) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md text-center"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Already Accepted</h1>
            <p className="text-muted-foreground mb-6">This invitation has already been accepted.</p>
            <Button asChild>
              <Link to="/links">Go to Dashboard</Link>
            </Button>
          </motion.div>
        </main>
      </div>
    );
  }

  const roleConfig = ROLE_CONFIG[invitation.role];
  const RoleIcon = roleConfig.icon;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Decorative background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orange-500/5 rounded-full blur-3xl" />
      </div>

      <Header />

      <main className="flex-1 flex items-center justify-center p-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-card rounded-2xl border shadow-lg p-8">
            {/* Workspace info */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl font-bold mb-1">Join {invitation.workspace.name}</h1>
              <p className="text-muted-foreground">
                You&apos;ve been invited to join this workspace
              </p>
            </div>

            {/* Role badge */}
            <div className="flex items-center justify-center gap-3 p-4 bg-muted/50 rounded-xl mb-6">
              <div
                className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center',
                  roleConfig.color
                )}
              >
                <RoleIcon className="h-5 w-5" />
              </div>
              <div className="text-left">
                <p className="font-medium">You&apos;ll join as {roleConfig.label}</p>
                <p className="text-sm text-muted-foreground">{roleConfig.description}</p>
              </div>
            </div>

            {/* Email info */}
            <div className="text-center text-sm text-muted-foreground mb-6">
              Invitation sent to{' '}
              <span className="font-medium text-foreground">{invitation.email}</span>
            </div>

            {/* Action based on auth state */}
            {isAuthenticated ? (
              emailMatches ? (
                // Logged in with matching email - show accept button
                <div className="space-y-4">
                  <Button
                    onClick={handleAccept}
                    disabled={isAccepting}
                    className="w-full h-12 text-base gap-2"
                  >
                    {isAccepting ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <CheckCircle className="h-5 w-5" />
                    )}
                    Accept & Join Workspace
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    Signed in as {user?.email}
                  </p>
                </div>
              ) : (
                // Logged in with different email - show warning
                <div className="space-y-4">
                  <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-900 dark:text-amber-100">
                          Email mismatch
                        </p>
                        <p className="text-sm text-amber-800/80 dark:text-amber-200/80">
                          You&apos;re signed in as{' '}
                          <span className="font-medium">{user?.email}</span>, but this invitation
                          was sent to <span className="font-medium">{invitation.email}</span>.
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" onClick={handleSignOut} className="w-full gap-2">
                    <LogOut className="h-4 w-4" />
                    Sign out and use a different account
                  </Button>
                </div>
              )
            ) : (
              // Not logged in - show login/signup options
              <div className="space-y-4">
                <Button asChild className="w-full h-12 text-base gap-2">
                  <Link
                    to={`/login?redirect=/invite/${token}&email=${encodeURIComponent(invitation.email)}`}
                  >
                    <LogIn className="h-5 w-5" />
                    Sign In to Accept
                  </Link>
                </Button>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">or</span>
                  </div>
                </div>
                <p className="text-sm text-center text-muted-foreground">
                  Don&apos;t have an account?{' '}
                  <Link to="/" className="text-primary font-medium hover:underline">
                    Request access
                  </Link>
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}

function Header() {
  return (
    <header className="relative z-10 border-b border-border bg-card/80 backdrop-blur-xl">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 font-bold text-xl group">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-accent/30 rounded-xl blur-sm group-hover:blur-md transition-all" />
            <img
              src="/assets/logo.png"
              alt="Armenian Link"
              className="relative w-9 h-9 rounded-xl shadow-sm group-hover:scale-105 transition-transform"
            />
          </div>
          <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Armenian Link
          </span>
        </Link>
      </div>
    </header>
  );
}
